/**
 * /chat 답변 속 차트(```wq-chart 펜스) 데이터 해석.
 *
 * 모델은 숫자 비교·추이가 핵심인 답변에서 아래 JSON 을 펜스에 담는다(백엔드 chat_session._get_chart_guide).
 *   {"type":"bar"|"line","title":"…","labels":["1반","2반"],"series":[{"name":"평균","data":[72,80]}],"unit":"점","source":"…"}
 * 화면은 차트로 그리고, 복사·읽어주기·익명 기록처럼 글만 다루는 곳은 같은 숫자를 표 글로 바꾼다.
 * 모양이 틀리면 차트를 그리지 않는다 — 잘못 그린 차트는 표보다 해롭다.
 */

export const CHART_FENCE_LANG = 'wq-chart';
export const CHART_TYPES = ['bar', 'line'];
export const CHART_MAX_LABELS = 24;
export const CHART_MAX_SERIES = 4;

const TITLE_MAX = 80;
const NAME_MAX = 40;
const LABEL_MAX = 40;
const UNIT_MAX = 12;
const SOURCE_MAX = 160;

const clip = (value, max) => String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, max);

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const cleaned = value.replace(/,/g, '').replace(/−/g, '-').trim();
  if (!/^[-+]?\d+(?:\.\d+)?$/.test(cleaned)) return undefined;
  return Number(cleaned);
};

/**
 * @param {string} raw 펜스 안 글
 * @returns {{ ok: true, spec: object } | { ok: false, error: string }}
 */
export function parseChartSpec(raw) {
  let data;
  try {
    data = JSON.parse(String(raw || '').trim());
  } catch (error) {
    return { ok: false, error: 'json' };
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return { ok: false, error: 'shape' };

  const type = String(data.type || 'bar').toLowerCase();
  if (!CHART_TYPES.includes(type)) return { ok: false, error: 'type' };

  const title = clip(data.title, TITLE_MAX);
  if (!title) return { ok: false, error: 'title' };

  if (!Array.isArray(data.labels)) return { ok: false, error: 'labels' };
  const labels = data.labels.map((label) => clip(label, LABEL_MAX));
  if (labels.length < 2 || labels.length > CHART_MAX_LABELS || labels.some((label) => !label)) {
    return { ok: false, error: 'labels' };
  }

  if (!Array.isArray(data.series) || data.series.length < 1 || data.series.length > CHART_MAX_SERIES) {
    return { ok: false, error: 'series' };
  }
  const series = [];
  for (let i = 0; i < data.series.length; i += 1) {
    const item = data.series[i];
    if (!item || !Array.isArray(item.data) || item.data.length !== labels.length) return { ok: false, error: 'series' };
    const values = item.data.map(toNumber);
    if (values.some((value) => value === undefined)) return { ok: false, error: 'value' };
    if (values.every((value) => value === null)) return { ok: false, error: 'value' };
    series.push({ name: clip(item.name, NAME_MAX) || (data.series.length === 1 ? '값' : `계열 ${i + 1}`), data: values });
  }

  return {
    ok: true,
    spec: {
      type,
      title,
      labels,
      series,
      unit: clip(data.unit, UNIT_MAX),
      source: clip(data.source, SOURCE_MAX),
    },
  };
}

const formatValue = (value) => (value === null ? '-' : value.toLocaleString('ko-KR', { maximumFractionDigits: 4 }));

const escapeCell = (text) => String(text).replace(/\|/g, '\\|');

/** 차트와 같은 숫자를 마크다운 표로 — 복사·읽어주기·화면 '표로 보기'에 쓴다. */
export function chartSpecToRows(spec) {
  const unit = spec.unit ? ` (${spec.unit})` : '';
  const header = ['항목', ...spec.series.map((item) => `${item.name}${unit}`)];
  const rows = spec.labels.map((label, index) => [label, ...spec.series.map((item) => formatValue(item.data[index]))]);
  return { header, rows };
}

export function chartSpecToMarkdownTable(spec) {
  const { header, rows } = chartSpecToRows(spec);
  const line = (cells) => `| ${cells.map(escapeCell).join(' | ')} |`;
  return [
    `**${spec.title}**`,
    '',
    line(header),
    line(header.map(() => '---')),
    ...rows.map(line),
    ...(spec.source ? ['', `출처: ${spec.source}`] : []),
  ].join('\n');
}

const CHART_FENCE_RE = /^ {0,3}(`{3,}|~{3,})[ \t]*wq-chart[ \t]*\n([\s\S]*?)\n {0,3}\1[ \t]*$/gm;

/**
 * 글만 다루는 경로용: wq-chart 펜스를 같은 숫자의 표로 바꾼다. 읽을 수 없는 펜스는 지운다(JSON 을 그대로 보이지 않게).
 * 닫히지 않은 펜스(스트리밍 중)는 손대지 않는다.
 */
export function replaceChartFences(markdown) {
  const text = typeof markdown === 'string' ? markdown : '';
  if (!text.includes(CHART_FENCE_LANG)) return text;
  return text
    .replace(CHART_FENCE_RE, (match, fence, body) => {
      const parsed = parseChartSpec(body);
      return parsed.ok ? chartSpecToMarkdownTable(parsed.spec) : '';
    })
    .replace(/\n{3,}/g, '\n\n');
}
